// ComplianceChecker.js - Automated monitoring and validation

import { db } from '../../services/firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, doc } from 'firebase/firestore';

export class ComplianceChecker {
  
  // Check for duplicate images across contributors
  static async checkImageDuplication(newImageUrl, contributorId) {
    try {
      const productsQuery = query(
        collection(db, 'contributorProducts'),
        where('imageUrl', '==', newImageUrl)
      );
      
      const existingProducts = await getDocs(productsQuery);
      const duplicates = [];
      
      existingProducts.forEach(doc => {
        const data = doc.data();
        if (data.userId !== contributorId) {
          duplicates.push({
            productId: doc.id,
            contributorId: data.userId,
            title: data.title
          });
        }
      });
      
      return {
        hasDuplicates: duplicates.length > 0,
        duplicates,
        riskLevel: duplicates.length > 0 ? 'HIGH' : 'LOW'
      };
    } catch (error) {
      console.error('Error checking image duplication:', error);
      return { hasDuplicates: false, duplicates: [], riskLevel: 'UNKNOWN' };
    }
  }

  // Validate image URLs for suspicious patterns
  static validateImageSource(imageUrl, productUrl) {
    const warnings = [];
    const riskFactors = [];
    
    try {
      const imageUrlObj = new URL(imageUrl);
      const productUrlObj = new URL(productUrl);
      
      // Check if image is hosted on competitor platforms
      const competitorDomains = [
        'ebay.com', 'alibaba.com', 'aliexpress.com', 
        'wish.com', 'walmart.com', 'target.com'
      ];
      
      competitorDomains.forEach(domain => {
        if (imageUrlObj.hostname.includes(domain)) {
          riskFactors.push(`Image hosted on competitor platform: ${domain}`);
        }
      });
      
      // Check if image and product URLs are from different domains
      if (imageUrlObj.hostname !== productUrlObj.hostname) {
        const knownSafeCombos = [
          // Amazon affiliate with Amazon images
          { product: 'amazon.com', image: 'm.media-amazon.com' },
          { product: 'amazon.com', image: 'images-na.ssl-images-amazon.com' },
          // Etsy with Etsy CDN
          { product: 'etsy.com', image: 'i.etsystatic.com' },
          // Shopify with Shopify CDN
          { product: 'shopify.com', image: 'cdn.shopify.com' }
        ];
        
        const isKnownSafe = knownSafeCombos.some(combo => 
          productUrlObj.hostname.includes(combo.product) && 
          imageUrlObj.hostname.includes(combo.image)
        );
        
        if (!isKnownSafe) {
          warnings.push('Image and product from different domains - verify ownership');
        }
      }
      
      // Check for stock photo watermarks or signatures
      const suspiciousPatterns = [
        'shutterstock', 'getty', 'watermark', 'stock',
        'dreamstime', 'fotolia', 'istockphoto'
      ];
      
      suspiciousPatterns.forEach(pattern => {
        if (imageUrl.toLowerCase().includes(pattern)) {
          riskFactors.push(`Possible stock photo detected: ${pattern}`);
        }
      });
      
      return {
        isValid: riskFactors.length === 0,
        warnings,
        riskFactors,
        riskLevel: riskFactors.length > 0 ? 'HIGH' : warnings.length > 0 ? 'MEDIUM' : 'LOW'
      };
      
    } catch (error) {
      return {
        isValid: false,
        warnings: ['Invalid URL format'],
        riskFactors: ['Malformed URL'],
        riskLevel: 'HIGH'
      };
    }
  }

  // Check for price inconsistencies
  static async checkPriceConsistency(productUrl, reportedPrice) {
    // This would require web scraping which has its own legal considerations
    // For now, just flag suspicious pricing patterns
    
    const warnings = [];
    
    if (reportedPrice < 5) {
      warnings.push('Unusually low price - verify not a pricing error');
    }
    
    if (reportedPrice > 10000) {
      warnings.push('High-value item - extra verification recommended');
    }
    
    return {
      warnings,
      requiresManualReview: warnings.length > 0
    };
  }

  // Check for prohibited content
  static checkContentCompliance(title, description, tags) {
    const prohibitedTerms = [
      // Counterfeit indicators
      'replica', 'knockoff', 'fake', 'copy', 'inspired by',
      // Misleading terms
      'authentic' + 'style', 'like new', 'as seen on',
      // Adult content
      'adult', 'mature', 'explicit',
      // Medical claims
      'cure', 'heal', 'medical', 'therapeutic'
    ];
    
    const suspiciousTerms = [
      'limited time', 'hurry', 'act now', 'going fast',
      'compare to', 'similar to', 'version of'
    ];
    
    const violations = [];
    const warnings = [];
    
    const fullText = `${title} ${description} ${tags.join(' ')}`.toLowerCase();
    
    prohibitedTerms.forEach(term => {
      if (fullText.includes(term.toLowerCase())) {
        violations.push(`Prohibited term detected: "${term}"`);
      }
    });
    
    suspiciousTerms.forEach(term => {
      if (fullText.includes(term.toLowerCase())) {
        warnings.push(`Suspicious marketing term: "${term}"`);
      }
    });
    
    return {
      isCompliant: violations.length === 0,
      violations,
      warnings,
      requiresReview: violations.length > 0 || warnings.length > 2
    };
  }

  // Comprehensive product validation
  static async validateProduct(product, contributorId) {
    const results = {
      productId: product.id || 'new',
      contributorId,
      timestamp: new Date(),
      checks: {}
    };
    
    // Image duplication check
    results.checks.imageDuplication = await this.checkImageDuplication(
      product.imageUrl, 
      contributorId
    );
    
    // Image source validation
    results.checks.imageSource = this.validateImageSource(
      product.imageUrl, 
      product.productUrl
    );
    
    // Price consistency
    results.checks.pricing = await this.checkPriceConsistency(
      product.productUrl, 
      product.price
    );
    
    // Content compliance
    results.checks.content = this.checkContentCompliance(
      product.title,
      product.description,
      product.tags || []
    );
    
    // Calculate overall risk score
    const riskFactors = [
      results.checks.imageDuplication.riskLevel === 'HIGH' ? 3 : 0,
      results.checks.imageSource.riskLevel === 'HIGH' ? 2 : 
      results.checks.imageSource.riskLevel === 'MEDIUM' ? 1 : 0,
      results.checks.pricing.requiresManualReview ? 1 : 0,
      !results.checks.content.isCompliant ? 3 : 
      results.checks.content.requiresReview ? 1 : 0
    ];
    
    const totalRisk = riskFactors.reduce((sum, risk) => sum + risk, 0);
    
    results.overallRisk = totalRisk >= 5 ? 'HIGH' : 
                         totalRisk >= 3 ? 'MEDIUM' : 'LOW';
    
    results.requiresManualReview = totalRisk >= 3;
    results.autoApprovalEligible = totalRisk === 0;
    
    return results;
  }

  // Log compliance check results
  static async logComplianceCheck(results) {
    try {
      await addDoc(collection(db, 'complianceChecks'), {
        ...results,
        createdAt: new Date()
      });
      
      // If high risk, also create an admin alert
      if (results.overallRisk === 'HIGH') {
        await addDoc(collection(db, 'adminAlerts'), {
          type: 'compliance_violation',
          severity: 'high',
          contributorId: results.contributorId,
          productId: results.productId,
          summary: this.generateAlertSummary(results),
          createdAt: new Date(),
          resolved: false
        });
      }
    } catch (error) {
      console.error('Error logging compliance check:', error);
    }
  }

  // Generate summary for admin alerts
  static generateAlertSummary(results) {
    const issues = [];
    
    if (results.checks.imageDuplication.hasDuplicates) {
      issues.push('Duplicate image detected');
    }
    
    if (results.checks.imageSource.riskLevel === 'HIGH') {
      issues.push('Suspicious image source');
    }
    
    if (!results.checks.content.isCompliant) {
      issues.push('Content compliance violations');
    }
    
    return issues.join(', ') || 'Multiple risk factors detected';
  }

  // Batch check for existing products (for auditing)
  static async auditExistingProducts(contributorId) {
    try {
      const productsQuery = query(
        collection(db, 'contributorProducts'),
        where('userId', '==', contributorId),
        where('status', '==', 'approved')
      );
      
      const products = await getDocs(productsQuery);
      const auditResults = [];
      
      for (const productDoc of products.docs) {
        const product = { id: productDoc.id, ...productDoc.data() };
        const validation = await this.validateProduct(product, contributorId);
        auditResults.push(validation);
        
        // Update product with compliance score
        if (validation.overallRisk !== 'LOW') {
          await updateDoc(doc(db, 'contributorProducts', product.id), {
            complianceRisk: validation.overallRisk,
            lastComplianceCheck: new Date(),
            requiresReview: validation.requiresManualReview
          });
        }
      }
      
      return auditResults;
    } catch (error) {
      console.error('Error auditing products:', error);
      return [];
    }
  }
}

export default ComplianceChecker;